import { useState, useRef, useEffect, MouseEvent } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import Lottie from 'lottie-react';
import Skeleton from '@mui/material/Skeleton';
import type { Business } from '@/modules/dashboard/dashboardTypes';
import type { Email } from '@/modules/email/emailTypes';
import isEmpty from 'lodash/isEmpty';
import {
	emailSendEmailsRequest,
	emailClearEmailsStatus,
	emailGetEmailStatuses
} from '@/modules/email/emailActions';
import { startNylasUser } from '@/modules/emailOnboarding/emailOnboardingActions';
import type { RootState } from '@/rootReducer';
import {
	PlusGold,
	TrashNew,
	ExclamationMarkRed,
	ExclamationMarkBlack,
	LeftArrow,
	CheckmarkIconBlack,
	EnvelopeGrey,
	Compose,
	Megaphone,
	EnvelopeCommunications,
	ExclamationMarkRedUnfilled,
	CloseThin
} from '@/assets/images';
import { sendAnalytics } from '@/services/analyticsService';
import loadingAnimation from './loading-animation.json';
import EmailsItem from './EmailsItem/EmailsItem';
import './BusinessEmailsTab.scss';
import { UserRole } from '@/modules/user/userTypes';

interface BusinessEmailsTabProps {
	business: Business;
	openReportIncorrectDataModal?: (x: string) => void;
	openComposeEmail: (b: Business, e: string) => void;
	getEmailThread: (b: Business) => void;
	onSelectTab: (tabValue: string) => void;
}

const BusinessEmailsTab = ({
	business,
	getEmailThread,
	openComposeEmail,
	onSelectTab
}: BusinessEmailsTabProps): JSX.Element => {
	const [
		selectedBusiness,
		getEmailThreadLoading,
		listSelected,
		user,
		emailLoading,
		emailSuccess,
		emailFailure
	] = useSelector(
		(state: RootState) => [
			state.dashboard.getIn(['businesses', 'selected']).toJS(),
			state.dashboard.getIn([
				'businesses',
				'selected',
				'threadedEmailsLoading'
			]),
			state.dashboard.getIn(['lists', 'selected']).toJS(),
			state.user.getIn(['authorize', 'user']).toJS(),
			state.email.getIn(['email', 'isLoading']),
			state.email.getIn(['email', 'success']),
			state.email.getIn(['email', 'failure'])
		],
		shallowEqual
	);

	const [message, setMessage] = useState<string>('');
	const [showValidation, setShowValidation] = useState<boolean>(false);
	const [showComposeEmail, setShowComposeEmail] = useState<boolean>(false);
	const [showPreview, setShowPreview] = useState<boolean>(false);
	const [showEmailReplyButton, setShowEmailReplyButton] =
		useState<boolean>(true);

	const dispatch = useDispatch();

	const sendEmails = (emails: Email[], isReply = false) =>
		dispatch(emailSendEmailsRequest({ emails, isReply }));

	const clearEmailsStatus = () => dispatch(emailClearEmailsStatus());

	const startEmailSignup = (payload: { user: any }) =>
		dispatch(startNylasUser(payload));
	const getEmailStatuses = () => dispatch(emailGetEmailStatuses());

	const inputWrapperRef = useRef<HTMLDivElement>(null);
	const previewRef = useRef<HTMLDivElement>(null);
	const validateDebounce = useRef<ReturnType<typeof setTimeout>>();

	const lastReceivedEmail = () => {
		if (selectedBusiness.threadedEmails?.length) {
			for (
				let i = selectedBusiness.threadedEmails?.length - 1;
				i >= 0;
				i--
			) {
				if (
					selectedBusiness.threadedEmails[
						i
					]?.from[0]?.email?.toLowerCase() !== user.email &&
					selectedBusiness.threadedEmails[
						i
					]?.from[0].email?.toLowerCase() !==
						user?.re2HostedEmail?.hostedEmail?.toLowerCase()
				) {
					// Will only affect hosted email users (Kimco)
					if (user?.re2HostedEmail) {
						const re = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+)/;

						// FIXME: ForwardedFrom logic is very convoluted. Can be simplified.
						const forwardedFrom = selectedBusiness.threadedEmails[
							i
						]?.snippet?.match(re)
							? selectedBusiness.threadedEmails[
									i
								]?.snippet?.match(re)[0]
							: selectedBusiness.threadedEmails[i]?.to[0]?.email;

						selectedBusiness.threadedEmails[i].forwardedFrom =
							forwardedFrom;
						selectedBusiness.threadedEmails[i].forwardedName =
							selectedBusiness.threadedEmails[0].to[0].name;

						return selectedBusiness.threadedEmails[i];
					}

					return selectedBusiness.threadedEmails[i];
				}
			}

			if (
				user?.re2HostedEmail &&
				selectedBusiness.threadedEmails[
					selectedBusiness.threadedEmails?.length - 1
				]?.from[0]?.email.toLowerCase() ==
					user?.re2HostedEmail?.hostedEmail.toLowerCase()
			) {
				const re = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+)/;
				if (
					selectedBusiness.threadedEmails[
						selectedBusiness.threadedEmails?.length - 1
					].snippet?.match(re)
				) {
					const forwardedFrom =
						selectedBusiness.threadedEmails[
							selectedBusiness.threadedEmails?.length - 1
						].snippet?.match(re)[0];
					selectedBusiness.threadedEmails[
						selectedBusiness.threadedEmails?.length - 1
					].forwardedFrom = forwardedFrom;
					selectedBusiness.threadedEmails[
						selectedBusiness.threadedEmails?.length - 1
					].forwardedName =
						selectedBusiness.threadedEmails[0].to[0].name;
				}
				return selectedBusiness.threadedEmails[
					selectedBusiness.threadedEmails.length - 1
				];
			}
		}
		return undefined;
	};

	const lastThreadedEmail = selectedBusiness.threadedEmails?.[0];

	const displayEmails = () => {
		const emailList = [];
		if (
			selectedBusiness.threadedEmails &&
			selectedBusiness.threadedEmails?.length > 1
		) {
			for (
				let i = selectedBusiness.threadedEmails?.length - 2;
				i >= 0;
				i--
			) {
				if (
					selectedBusiness.threadedEmails[i].snippet.includes(
						'---------- Forwarded message ----------'
					)
				) {
					const re = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+)/;
					const forwardedFrom =
						selectedBusiness.threadedEmails[i].snippet?.match(
							re
						)?.[0];
					selectedBusiness.threadedEmails[i].forwardedFrom =
						forwardedFrom;
					selectedBusiness.threadedEmails[i].forwardedName =
						selectedBusiness.threadedEmails[0].to[0].name;
				}
				emailList.push(selectedBusiness.threadedEmails[i]);
			}
		}
		return emailList.map(email => (
			<EmailsItem key={Math.random()} email={email} />
		));
	};

	useEffect(() => {
		if (emailSuccess) {
			getEmailStatuses();

			setTimeout(() => {
				clearEmailsStatus();
				setShowPreview(false);
				setShowComposeEmail?.(false);
				setMessage?.('');
				setShowEmailReplyButton?.(true);

				if (getEmailThread) {
					getEmailThread(business);
				}
			}, 2000);
		}
	}, [emailSuccess]);

	useEffect(() => {
		return () => {
			clearEmailsStatus();
			setShowPreview(false);
			setShowComposeEmail?.(false);
			setMessage?.('');
			setShowEmailReplyButton?.(true);
		};
	}, []);

	useEffect(() => {
		if (getEmailThread) {
			getEmailThread(business);
		}
	}, []);

	const isReply =
		user.email !==
			selectedBusiness.threadedEmails?.[
				selectedBusiness.threadedEmails?.length - 1
			]?.from[0]?.email &&
		selectedBusiness.threadedEmails?.[
			selectedBusiness.threadedEmails?.length - 1
		]?.from[0]?.email !== 're2 Email System';

	const sendEmail = (e: MouseEvent) => {
		const userAdded = {
			exists: false,
			index: null
		};

		if (
			business.userAddedData &&
			business.userAddedData.filter(d => {
				return (
					d.data === lastReceivedEmail()?.from[0]?.email ||
					d.data === lastThreadedEmail?.to[0]?.email
				);
			}).length > 0
		) {
			business.userAddedData.forEach((dataPt, i) => {
				if (
					dataPt.type === 'email' &&
					(dataPt.data === lastReceivedEmail()?.from[0]?.email ||
						dataPt.data === lastThreadedEmail?.to[0]?.email)
				) {
					userAdded.exists = true;
					userAdded.index = i;
				}
			});
		}

		e.preventDefault();
		e.stopPropagation();

		if (business) {
			const arr = message.split('\n');
			const newArr = arr.map(item => {
				return `<p key=${Math.random()}>${item}</p>\n`;
			});
			const newBody = newArr.join('');
			const email = user?.re2HostedEmail
				? selectedBusiness.threadedEmails[0].to[0].email
				: lastReceivedEmail()?.to[0]?.email ||
					lastThreadedEmail?.to[0]?.email;
			sendEmails(
				[
					{
						subject:
							lastReceivedEmail()?.subject ||
							lastThreadedEmail?.subject,
						body: newBody,
						businessId: business._id || business.businessId,
						business,
						name: business.name,
						listId: listSelected.id || listSelected._id,
						email,
						userAdded,
						conversationEmail: true
					}
				],
				isReply
			);
		}
	};

	const [showEmailFailed, setShowEmailFailed] = useState<boolean>(false);
	const [manuallyDismissed, setManuallyDismissed] = useState(false);

	useEffect(() => {
		if (manuallyDismissed) return;

		if (selectedBusiness?.emailStatus === 'failed') {
			setShowEmailFailed(true);
		} else {
			setShowEmailFailed(false);
		}
	}, [manuallyDismissed, selectedBusiness]);

	const showButton = () => {
		if (!showPreview) {
			return (
				<button
					className="compose-email-input-button"
					type="button"
					onClick={() => {
						if (message.trim().length > 0) {
							if (showValidation) {
								setShowValidation(false);
							}

							setShowPreview(true);
							setMessage(message.trim());
						} else if (
							message.trim().length === 0 &&
							!showValidation
						) {
							setShowValidation(true);

							if (validateDebounce.current) {
								clearTimeout(validateDebounce.current);
							}

							validateDebounce.current = setTimeout(() => {
								setShowValidation(false);
							}, 3000);
						}
					}}
				>
					Preview
				</button>
			);
		}

		if (showPreview && !emailFailure) {
			return (
				<button
					className="compose-email-input-button"
					type="button"
					onClick={e => {
						if (user && user.email) {
							sendAnalytics({
								type: 'track',
								userId: user.email,
								eventName: `User sent email using "Add Reply" button`,
								properties: {
									source:
										window.location.pathname ===
										'/communication-center'
											? 'Communication Center'
											: 'List View',
									businessId:
										business.businessId || business._id,
									businessName: business.name,
									sender: user.email,
									sendingMethod: user.re2HostedEmail
										? 'Hosted email'
										: 'Nylas'
								}
							});
						}
						sendEmail(e);
					}}
				>
					Send
				</button>
			);
		}

		return (
			<button
				className="compose-email-input-button"
				type="button"
				onClick={() => {
					setShowPreview?.(false);
					clearEmailsStatus();
				}}
			>
				Try Again
			</button>
		);
	};

	const replyButton = showEmailReplyButton ? (
		<button
			type="button"
			className="reply-button"
			onClick={() => {
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: `User pressed "Add Reply" button`,
						properties: {
							businessId: business?.businessId || business._id,
							businessName: business.name
						}
					});
				}
				setShowComposeEmail?.(true);
				setShowEmailReplyButton?.(false);
			}}
		>
			<span className="reply-button-inner-div">
				<PlusGold />
				{isReply ? 'Add Reply' : 'Follow Up'}
			</span>
		</button>
	) : (
		<></>
	);

	const checkEmails = () => {
		if (
			!selectedBusiness?.email &&
			!selectedBusiness?.facebook?.email &&
			!selectedBusiness?.web?.email
		) {
			if (
				!selectedBusiness?.userAddedData ||
				selectedBusiness?.userAddedData.length === 0
			) {
				return false;
			}

			for (let i = 0; i < selectedBusiness.userAddedData.length; i++) {
				if (selectedBusiness.userAddedData[i].type === 'email') {
					return true;
				}
			}
		}

		return true;
	};

	return (
		<>
			<div className="email-tab">
				{showEmailFailed && (
					<div className="email-failed">
						<span className="email-failed-left">
							<ExclamationMarkRedUnfilled />
							<span className="email-failed-text">
								Email failed to send
							</span>
						</span>

						<span className="email-failed-right">
							<button
								type="button"
								className="email-failed-retry"
								onClick={() => {
									if (selectedBusiness?.email) {
										openComposeEmail(
											selectedBusiness,
											selectedBusiness?.email
										);
									} else if (
										selectedBusiness?.facebook?.email
									) {
										openComposeEmail(
											selectedBusiness,
											selectedBusiness?.facebook?.email
										);
									} else if (selectedBusiness?.web?.email) {
										openComposeEmail(
											selectedBusiness,
											selectedBusiness?.web?.email
										);
									} else if (
										selectedBusiness?.userAddedData
									) {
										for (
											let i = 0;
											i <
											selectedBusiness.userAddedData
												.length;
											i++
										) {
											if (
												selectedBusiness.userAddedData[
													i
												].type === 'email'
											) {
												openComposeEmail(
													selectedBusiness,
													selectedBusiness
														.userAddedData[i].data
												);
												break;
											}
										}
									}
								}}
							>
								Retry
							</button>

							<button
								type="button"
								onClick={() => {
									setShowEmailFailed(false);
									setManuallyDismissed(true);
								}}
							>
								<CloseThin className="text-dm-white duration-300 hover:brightness-90" />
							</button>
						</span>
					</div>
				)}

				{getEmailThreadLoading && (
					<>
						<div className="skeleton-outer-div">
							<div className="skeleton-div-1">
								<Skeleton
									height={35}
									sx={{ bgcolor: '#505050' }}
								/>
							</div>

							<div className="skeleton-div-2">
								<Skeleton
									height={35}
									sx={{ bgcolor: '#505050' }}
								/>
							</div>

							<div className="skeleton-div-3">
								<Skeleton
									height={30}
									sx={{ bgcolor: '#505050' }}
								/>

								<span> </span>

								<Skeleton
									height={20}
									sx={{ bgcolor: '#505050' }}
								/>
							</div>

							<div className="skeleton-div-4">
								<Skeleton
									height={200}
									sx={{ bgcolor: '#505050' }}
								/>
							</div>
						</div>

						<div className="skeleton-outer-div">
							<div className="skeleton-div-1">
								<Skeleton
									height={35}
									sx={{ bgcolor: '#505050' }}
								/>
							</div>

							<div className="skeleton-div-2">
								<Skeleton
									height={35}
									sx={{ bgcolor: '#505050' }}
								/>
							</div>

							<div className="skeleton-div-3">
								<Skeleton
									height={30}
									sx={{ bgcolor: '#505050' }}
								/>

								<span> </span>

								<Skeleton
									height={20}
									sx={{ bgcolor: '#505050' }}
								/>
							</div>

							<div className="skeleton-div-4">
								<Skeleton
									height={200}
									sx={{ bgcolor: '#505050' }}
								/>
							</div>
						</div>
					</>
				)}

				{showComposeEmail && (
					<div className="compose-email">
						<div className="compose-email-header">
							<span className="compose-email-header-text">
								{lastReceivedEmail()?.subject ||
									lastThreadedEmail?.subject}
							</span>
						</div>

						<div className="compose-email-input-div">
							{emailSuccess && (
								<div className="compose-email-success">
									<CheckmarkIconBlack width={24} />
									Email sent
								</div>
							)}

							{emailFailure && (
								<div className="compose-email-failure">
									<ExclamationMarkBlack />
									Email failed to send
								</div>
							)}

							{emailLoading && (
								<div className="compose-email-loading">
									<span className="compose-email-loading-text">
										Sending email...
									</span>
									<span>Filler text</span>
									<Lottie
										animationData={loadingAnimation}
										loop
										style={{ width: 250, height: 250 }}
									/>
								</div>
							)}

							{!emailSuccess && !emailLoading && (
								<>
									<div
										className={`compose-email-input-header ${emailFailure ? 'failure' : ''}`}
									>
										{!showPreview && user.role !== UserRole.PreTrial ? (
											<div>
												<span className="compose-email-input-header-left">
													You
												</span>

												<span className="compose-email-input-header-right emails">
													Emails Left Today:{' '}
													{user.emailLimitLeft}
												</span>
											</div>
										) : (
											<button
												type="button"
												className="compose-email-input-header-preview-left"
												onClick={() => {
													setShowPreview(false);
													clearEmailsStatus();
												}}
											>
												<LeftArrow />
												Back to editing
											</button>
										)}

										<button
											type="button"
											className="compose-email-input-header-right"
											onClick={() => {
												setShowComposeEmail(false);
												setShowEmailReplyButton(true);
												setShowPreview(false);
												setMessage('');
											}}
										>
											<TrashNew />
											Discard
										</button>
									</div>

									{!showPreview && (
										<div
											className="compose-email-input-wrapper"
											ref={inputWrapperRef}
										>
											<span
												className={`compose-email-input-replica ${showValidation ? 'validation' : ''}`}
											>
												{message}
											</span>

											<textarea
												id="message"
												className="compose-email-input"
												placeholder="Type message here"
												onChange={e => {
													setMessage?.(
														e.target.value
													);

													if (
														e.target.value.trim()
															.length === 0 &&
														!showValidation
													) {
														if (
															validateDebounce.current
														) {
															clearTimeout(
																validateDebounce.current
															);
														}

														setShowValidation(true);

														validateDebounce.current =
															setTimeout(() => {
																setShowValidation(
																	false
																);
															}, 3000);
													}

													if (
														e.target.value.trim()
															.length > 0 &&
														showValidation
													) {
														if (
															validateDebounce.current
														) {
															clearTimeout(
																validateDebounce.current
															);
														}

														setShowValidation(
															false
														);
													}
												}}
												value={message}
											/>
										</div>
									)}

									<label
										htmlFor="message"
										className={`compose-email-input-validation ${showValidation ? 'show' : ''}`}
									>
										<ExclamationMarkRed className="compose-email-input-validation-icon" />
										Message body empty
									</label>

									{showPreview && (
										<div
											ref={previewRef}
											className="compose-email-preview"
										>
											<div className="compose-email-preview-from">
												<span className="compose-email-preview-from-label">
													From
												</span>

												<span className="compose-email-preview-from-text">
													{user?.re2HostedEmail &&
													lastReceivedEmail()?.subject.includes(
														'Fwd: Re:'
													)
														? `${user.name} <${user.email}>`
														: user?.re2HostedEmail
															? `${user.name} <${user?.re2HostedEmail?.hostedEmail}>`
															: `${user.name} <${user.email}>`}
												</span>
											</div>

											<div className="compose-email-preview-to">
												<span className="compose-email-preview-to-label">
													To
												</span>

												<span className="compose-email-preview-to-text">
													{user?.re2HostedEmail &&
													lastReceivedEmail()?.subject.includes(
														'Fwd: Re:'
													)
														? `${lastReceivedEmail()?.forwardedName || lastThreadedEmail?.to[0].name} <${lastReceivedEmail()?.forwardedFrom || lastThreadedEmail?.to[0].email}>`
														: user?.re2HostedEmail
															? `${selectedBusiness.threadedEmails[0].to[0].name} <${selectedBusiness.threadedEmails[0].to[0].email}>`
															: `${lastReceivedEmail()?.from[0].name || lastThreadedEmail?.to[0].name} <${lastReceivedEmail()?.from[0].email || lastThreadedEmail?.to[0].email}>`}
												</span>
											</div>

											<div className="compose-email-preview-subject">
												<div className="compose-email-preview-subject-label">
													Subject
												</div>

												<span className="compose-email-preview-subject-text">
													{lastReceivedEmail()?.subject?.replace(
														/^Fwd: /,
														''
													) ||
														lastThreadedEmail?.subject?.replace(
															/^Fwd: /,
															''
														)}
												</span>
											</div>

											<div className="compose-email-preview-body">
												{message}
											</div>
										</div>
									)}

									{showButton()}
								</>
							)}
						</div>
					</div>
				)}

				<ul>
					{selectedBusiness.threadedEmails?.length > 0 &&
					!getEmailThreadLoading ? (
						<>
							<EmailsItem
								key={Math.random()}
								email={
									selectedBusiness.threadedEmails[
										selectedBusiness.threadedEmails
											?.length - 1
									]
								}
								replyButton={replyButton}
							/>

							{displayEmails()}
						</>
					) : (
						!getEmailThreadLoading && (
							<div className="email-empty">
								<div className="email-empty-content">
									<EnvelopeGrey />
									<span className="email-empty-text-wide">
										Sorry, we could not retrieve this
										thread. Try searching your email inbox
										for the contacted email address.
									</span>
								</div>
							</div>
						)
					)}
				</ul>

				{((!business.nylasThreadId &&
					user?.threadedEmail &&
					!isEmpty(user?.threadedEmail) &&
					checkEmails() &&
					!selectedBusiness.threadedEmails?.length) ||
					(!business.nylasThreadId &&
						user?.re2HostedEmail &&
						!isEmpty(user?.re2HostedEmail) &&
						checkEmails() &&
						!selectedBusiness.threadedEmails?.length)) && (
					<div className="email-empty">
						<div className="email-empty-content">
							<EnvelopeGrey />
							<span className="email-empty-text">
								Get started by sending an email to{' '}
								{selectedBusiness.name}
							</span>
							<button
								type="button"
								className="email-empty-button"
								onClick={() => {
									if (selectedBusiness?.email) {
										openComposeEmail(
											selectedBusiness,
											selectedBusiness?.email
										);
									} else if (
										selectedBusiness?.facebook?.email
									) {
										openComposeEmail(
											selectedBusiness,
											selectedBusiness?.facebook?.email
										);
									} else if (selectedBusiness?.web?.email) {
										openComposeEmail(
											selectedBusiness,
											selectedBusiness?.web?.email
										);
									} else if (
										selectedBusiness?.userAddedData
									) {
										for (
											let i = 0;
											i <
											selectedBusiness.userAddedData
												.length;
											i++
										) {
											if (
												selectedBusiness.userAddedData[
													i
												].type === 'email'
											) {
												openComposeEmail(
													selectedBusiness,
													selectedBusiness
														.userAddedData[i].data
												);
												break;
											}
										}
									}
								}}
							>
								<Compose className="email-empty-button-icon" />
								Compose Message
							</button>
						</div>
					</div>
				)}

				{!business.nylasThreadId &&
					user?.threadedEmail &&
					!isEmpty(user?.threadedEmail) &&
					!checkEmails() && (
						<div className="email-empty">
							<div className="email-empty-content">
								<EnvelopeGrey />
								<span className="email-empty-text">
									Get started by contacting{' '}
									{selectedBusiness.name}
								</span>
								<button
									type="button"
									className="email-empty-button"
									onClick={() => onSelectTab('info')}
								>
									<Megaphone className="email-empty-button-icon" />
									Contact Business
								</button>
							</div>
						</div>
					)}

				{(!user?.threadedEmail || isEmpty(user?.threadedEmail)) &&
					(!user?.re2HostedEmail || isEmpty(user?.re2HostedEmail)) &&
					!user?.re2SharedDomain && (
						<div className="email-empty">
							<div className="email-empty-content">
								<EnvelopeGrey />
								<span className="email-empty-text">
									Verify your email address to get access to
									new features like email threading and
									replies.
								</span>
								<button
									type="button"
									className="email-empty-button"
									onClick={() => {
										startEmailSignup({ user });
									}}
								>
									<EnvelopeCommunications className="email-empty-button-icon envelope" />
									Verify your Email
								</button>
								<span className="email-empty-text-gmail">
									If you&apos;re using Gmail, you can
									disregard this message. This new
									functionality should be available to you
									soon!
								</span>
							</div>
						</div>
					)}
			</div>
		</>
	);
};

export default BusinessEmailsTab;
